import React from "react";

import {
  mdiAlertCircleOutline,
  mdiCloudUploadOutline,
  mdiLoading,
} from "@mdi/js";
import Icon from "@mdi/react";
import classnames from "classnames";
import { useTranslation } from "react-i18next";

import SourceFileCard from "./SourceFileCard";

import type { UploadMode } from "./types";
import type { PivotMedia, SourceMedia } from "../../../api/EncoderAPI.types";

interface SourceMediaPanelContentProps {
  open: () => void;
  isDragAccept: boolean;
  isDragReject: boolean;
  isUploadBlocked: boolean;
  isLoading?: boolean;
  isEncoding: boolean;
  isSimpleMode?: boolean;
  isUploading: boolean;
  isQueued: boolean;
  isUploadError: boolean;
  uploadProgress: number;
  currentFile: File | null;
  uploadedMimeTypeMap: Record<string, string>;
  sortedSourceMediaList: (SourceMedia | PivotMedia)[];
  pendingSource: SourceMedia | null;
  uploadMode: UploadMode;
  onSetSourceToDelete: (source: { uuid: string; filename: string }) => void;
  onResetUpload: () => void;
}

export default function SourceMediaPanelContent({
  open,
  isDragAccept,
  isDragReject,
  isUploadBlocked,
  isLoading,
  isEncoding,
  isSimpleMode,
  isUploading,
  isQueued,
  isUploadError,
  uploadProgress,
  currentFile,
  uploadedMimeTypeMap,
  sortedSourceMediaList,
  pendingSource,
  uploadMode,
  onSetSourceToDelete,
  onResetUpload,
}: SourceMediaPanelContentProps): React.JSX.Element {
  const { t } = useTranslation("editor-content");

  const hasContent = sortedSourceMediaList.length > 0 || pendingSource !== null;

  if (hasContent) {
    return (
      <div className="flex flex-col gap-[0.78125rem]">
        {sortedSourceMediaList.map((source) => (
          <SourceFileCard
            key={source.uuid}
            isEncoding={isEncoding}
            isSimpleMode={isSimpleMode}
            mimeType={uploadedMimeTypeMap[source.filename]}
            source={source}
            onDelete={() =>
              onSetSourceToDelete({
                uuid: source.uuid,
                filename: source.filename,
              })
            }
          />
        ))}
        {pendingSource ? (
          <SourceFileCard
            isEncoding={isEncoding}
            isQueued={isQueued}
            isSimpleMode={isSimpleMode}
            isUploadError={isUploadError}
            isUploading={isUploading}
            mimeType={currentFile?.type}
            onDelete={onResetUpload}
            source={pendingSource}
            uploadProgress={uploadProgress}
          />
        ) : null}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Icon className="text-subtle animate-spin" path={mdiLoading} size={2} />
      </div>
    );
  }

  const dropzoneAreaClassNames = classnames(
    "flex h-full flex-col items-center justify-center rounded-md border-2 border-dashed px-6.25 py-10 text-center transition-colors",
    {
      "border-input bg-muted cursor-not-allowed": isUploadBlocked,
      "border-status-active bg-status-active/10":
        !isUploadBlocked && isDragAccept,
      "border-destructive bg-destructive/10":
        !isUploadBlocked && isDragReject && !isDragAccept,
      "group cursor-pointer border-input hover:border-primary hover:bg-muted":
        !isUploadBlocked && !isDragAccept && !isDragReject,
    },
  );

  return (
    <div
      className={dropzoneAreaClassNames}
      onClick={isUploadBlocked ? undefined : open}
    >
      {isUploadBlocked ? (
        <React.Fragment>
          <Icon
            className="text-muted-foreground mb-2"
            path={mdiAlertCircleOutline}
            size={3}
          />
          <p className="text-md text-foreground font-medium">
            {t("uploadBlockedTitle")}
          </p>
          <p className="text-muted-foreground mt-1 text-xs">
            {t("uploadBlockedSubtitle")}
          </p>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <Icon
            className="group-hover:text-primary text-subtle mb-2 transition-colors"
            path={mdiCloudUploadOutline}
            size={3}
          />
          <p className="text-md text-foreground font-medium">
            {
              {
                audioOnly: t("dragAndDropAudio"),
                videoLocked: t("dragAndDropAudioOrSubtitle"),
                open: t("dragAndDropMedia"),
                none: undefined,
              }[uploadMode]
            }
          </p>
          <p className="text-muted-foreground mt-1 text-xs">
            {
              {
                audioOnly: t("supportsAudioOnly"),
                videoLocked: t("supportsAudioOrVTT"),
                open: t("supportsMedia"),
                none: undefined,
              }[uploadMode]
            }
          </p>
        </React.Fragment>
      )}
    </div>
  );
}
